// Stepper.tsx — the 5-step onboarding progress indicator (spec D6, handoff §6). Visual
// states per handoff: done = filled teal circle + ✓, current = teal outline + number,
// future = muted outline + number; connectors between circles fill teal once the step
// before them is done. Every circle is clickable (mirrors the design prototype's
// `obGo1..obGo5` handlers) — `Onboarding.tsx` is the one that decides whether a jump is
// actually safe (its botId/bot-fetch bounce-to-step-1 guard runs regardless of how `step`
// changed). Transitions respect `prefers-reduced-motion` (motion-reduce: variants below).
//
// Locale-dashboard plan, Task 6 — step labels and the mobile/aria copy come from `useT()`;
// `STEPS` below is built inside the component so it can read `t`.
import { useT } from '../../locales/context';

export type StepId = 1 | 2 | 3 | 4 | 5;

interface StepMeta {
  id: StepId;
  label: string;
}

type StepState = 'done' | 'current' | 'future';

function stateFor(id: StepId, current: StepId): StepState {
  if (id < current) return 'done';
  if (id === current) return 'current';
  return 'future';
}

const CIRCLE_CLASS: Record<StepState, string> = {
  done: 'border-primary bg-primary text-primary-foreground',
  current: 'border-primary text-primary',
  future: 'border-border text-muted-foreground',
};

export function Stepper({
  current,
  onNavigate,
}: {
  current: StepId;
  onNavigate: (step: StepId) => void;
}) {
  const t = useT();
  const STEPS: StepMeta[] = [
    { id: 1, label: t.onboarding.stepWebsite },
    { id: 2, label: t.onboarding.stepCrawl },
    { id: 3, label: t.onboarding.stepTryIt },
    { id: 4, label: t.onboarding.stepLook },
    { id: 5, label: t.onboarding.stepGoLive },
  ];
  const currentMeta = STEPS.find((step) => step.id === current)!;
  return (
    <>
      {/* <640px: the 5-circle rail (each circle + label pair) is wider than a phone
       * viewport, so it's replaced with a compact readout instead of shrinking further. */}
      <div className="sm:hidden">
        <p className="text-[12.5px] font-medium text-foreground">
          {t.onboarding.stepMobileReadout({ current, total: STEPS.length, label: currentMeta.label })}
        </p>
        <div
          role="progressbar"
          aria-valuenow={current}
          aria-valuemin={1}
          aria-valuemax={STEPS.length}
          aria-label={t.onboarding.stepProgressAriaLabel({
            current,
            total: STEPS.length,
            label: currentMeta.label,
          })}
          className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted"
        >
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-150 motion-reduce:transition-none"
            style={{ width: `${(current / STEPS.length) * 100}%` }}
          />
        </div>
      </div>
      <div
        className="hidden items-center sm:flex"
        role="tablist"
        aria-label={t.onboarding.stepTablistAriaLabel}
      >
        {STEPS.map((step, i) => {
          const state = stateFor(step.id, current);
          // Future steps are NOT clickable — jumping ahead of the current step would land on
          // a screen that depends on state the wizard hasn't produced yet (e.g. `Onboarding.tsx`'s
          // own botId/bot-fetch guard would just bounce back to step 1 anyway). Rendered as a
          // disabled button instead of a plain `<div>` so it keeps the same tab semantics
          // (`role="tab"`) and layout as the clickable states, just inert.
          const circleAndLabel = (
            <>
              <span
                aria-hidden="true"
                className={`grid h-[26px] w-[26px] shrink-0 place-items-center rounded-full border-[1.5px] text-[12.5px] font-semibold transition-colors duration-150 motion-reduce:transition-none ${CIRCLE_CLASS[state]}`}
              >
                {state === 'done' ? '✓' : step.id}
              </span>
              <span
                className={`text-[12.5px] font-medium whitespace-nowrap ${state === 'future' ? 'text-muted-foreground' : 'text-foreground'}`}
              >
                {step.label}
              </span>
            </>
          );
          return (
            <div className="contents" key={step.id}>
              <button
                type="button"
                role="tab"
                aria-selected={state === 'current'}
                aria-current={state === 'current' ? 'step' : undefined}
                disabled={state === 'future'}
                onClick={state === 'future' ? undefined : () => onNavigate(step.id)}
                className={`flex items-center gap-2 bg-transparent ${state === 'future' ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              >
                {circleAndLabel}
              </button>
              {i < STEPS.length - 1 && (
                <span
                  aria-hidden="true"
                  className="mx-2.5 h-[1.5px] flex-1 transition-colors duration-150 motion-reduce:transition-none"
                  style={{
                    backgroundColor: step.id < current ? 'var(--primary)' : 'var(--border)',
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
